import React, { useState } from "react";
import { pipe } from "fp-ts/lib/function";
import { array as extArray } from "../../../../shared/src/utilByDomainGroupExport";
import { isInvalidDate } from "../../../../shared/src/util";
import { TFormStatus } from "./../../../../shared/src/codecs/codec";
import { CRMPartyMemberForm } from "../CRMPartyMemberForm/CRMPartyMemberForm";
import { CRMButtonIcon } from "../CRMButtonIcon/CRMButtonIcon";
import { CRMFormButtonIconWithLabel } from "../CRMFormButtonIconWithLabel/CRMFormButtonIconWithLabel";
import { CRMParagraph } from "../Simple/CRMParagraph/CRMParagraph";
import { CRMPadding } from "../Simple/CRMPadding/CRMPadding";
import { TextCenter } from "../TextCenter/TextCenter";
import { WeightBold } from "../WeightBold/WeightBold";
import { TCaseMemberChildrenForm, TCaseMemberCreateIdCheckForm, TCaseMemberForm, TCaseMemberIdCheckForm, TCaseMemberReScreenIdCheckForm, TCaseMemberRunScreeningIdCheckForm, TCaseMemberSourceOfWealthForm, TCaseMemberNoDigitalContactChannelExemptionForm, TCaseMemberSuggestedSimilarUserForm, TCaseSendInstructedEmailForm, TCaseMemberFlagNeedsVideoCallForm } from "../../../../domain/codecs/form/CaseMemberForm";
import { TPhoneNumberForm } from "../../../../domain/codecs/form/PhoneNumberForm";
import { editFormEditableValue } from "../../../../shared/src/codecs/types/formEditable";
import { CRMVerticalTabs } from "../Complex/CRMVerticalTabs/CRMVerticalTabs";
import { array, ord } from "fp-ts";
import { Ord as NumberOrd } from "fp-ts/lib/number";
import { Ord as StringOrd } from "fp-ts/lib/string";
import { CRMCardOutsidePopupBasic } from "../CRMCardOutsidePopupBasic/CRMCardOutsidePopupBasic";
import { TCaseEditableFull } from "../../../../domain/codecs/Cases";
import { CRMCardInside } from "../CRMCardInside/CRMCardInside";
import { PartyGroupOrder, TUserCaseUserTypePartyGroup, UserCaseUserTypePartyGroup_Tone, UserCaseUserType_PartyGroup } from "../../../../domain/codecs/UserCaseUserType";

type TPartyMembersFormProps = {
    case: TCaseEditableFull;
    transactionType: string;
    sellerReasonForSale: string;
    caseIsInstructed: boolean;
    members: Array<TCaseMemberForm>;
    isNotAllowedToReceiveInstructEmailDueToDualRepConsent: boolean;
    onAddMember: () => void;
    onRemoveMember: (member: TCaseMemberForm) => void;
    onUpdateMembers: (members: Array<TCaseMemberForm>) => void;
    onUpdateMemberChild: (memberChild: TCaseMemberChildrenForm) => void;
    onMemberSendInstructedEmailClick: (value: TCaseSendInstructedEmailForm) => void;
    createPhoneNumberStatus: TFormStatus;
    onAddNewPhoneNumber: (memberId: string) => void;
    onRemovePhoneNumber: (form: TPhoneNumberForm) => void;
    onChildIdChecksChange: (value: TCaseMemberIdCheckForm) => void;
    onNewIdCheck: (value: TCaseMemberCreateIdCheckForm) => void;
    onReScreenIdCheck: (value: TCaseMemberReScreenIdCheckForm) => void;
    onRunScreeningIdCheck: (value: TCaseMemberRunScreeningIdCheckForm) => void;
    onFlagNeedsVideoCall: (value: TCaseMemberFlagNeedsVideoCallForm) => void;
    onSourceOfWealthChange: (value: TCaseMemberSourceOfWealthForm) => void;
    onNoDigitalContactChannelExemptionChange: (value: TCaseMemberNoDigitalContactChannelExemptionForm) => void;
    onSwapMember: (form: TCaseMemberSuggestedSimilarUserForm) => void;
    canSignOffRoadblocks?: boolean;
};

const memberGroup = (member: TCaseMemberForm): TUserCaseUserTypePartyGroup =>
    UserCaseUserType_PartyGroup(member.children.edited.user_type);

const memberSortKey = (member: TCaseMemberForm): [number, number, string] => [
    PartyGroupOrder.indexOf(memberGroup(member)),
    member.edited.is_primary_contact ? 0 : 1,
    `${member.edited.last_name} ${member.edited.first_name}`.toLowerCase(),
];

const sortMembersByGroup = (members: Array<TCaseMemberForm>): Array<TCaseMemberForm> =>
    pipe(
        members,
        array.sortBy([
            ord.contramap((member: TCaseMemberForm) => memberSortKey(member)[0])(NumberOrd),
            ord.contramap((member: TCaseMemberForm) => memberSortKey(member)[1])(NumberOrd),
            ord.contramap((member: TCaseMemberForm) => memberSortKey(member)[2])(StringOrd),
        ]),
    );

const groupSummary = (members: Array<TCaseMemberForm>): string =>
    pipe(
        PartyGroupOrder,
        array.map((group) => ({
            group,
            count: members.filter((member) => memberGroup(member) === group).length,
        })),
        array.filter(({ count }) => count > 0),
        array.map(({ group, count }) => `${count} ${group.toLowerCase()}`),
        (parts) => parts.join(" · "),
    );

export const CRMPartyMembersForm = (props: TPartyMembersFormProps): JSX.Element => {
    const [memberToBeDeleted, setMemberToBeDeleted] = useState<TCaseMemberForm | null>(null);

    const sortedMembers = sortMembersByGroup(props.members);

    return <>
        <CRMCardInside>
            <CRMPadding size="medium">
                <CRMParagraph>
                    NB: Editing the client on all cases.
                </CRMParagraph>
            </CRMPadding>
        </CRMCardInside>

        {sortedMembers.length > 0 &&
            <CRMCardInside>
                <CRMPadding size="medium">
                    <CRMParagraph>
                        <WeightBold>
                            {groupSummary(sortedMembers)}
                        </WeightBold>
                    </CRMParagraph>
                </CRMPadding>
            </CRMCardInside>
        }

        <CRMVerticalTabs
            tabs={pipe(
                sortedMembers,
                array.map((member) => ({
                    tabLabel: `${member.edited.first_name} ${member.edited.last_name}`,
                    labelIcon:
                        !isInvalidDate(member.edited.case_reminders_paused_until) ? "snooze"
                            : member.edited.is_primary_contact ? "person-tick"
                                : undefined,
                    tabGroup: {
                        label: memberGroup(member),
                        tone: UserCaseUserTypePartyGroup_Tone(memberGroup(member)),
                    },
                    tabOptions: [
                        {
                            label: "Set as primary contact",
                            onClick: () => props.onUpdateMembers(
                                pipe(
                                    props.members,
                                    extArray.updateWhere(
                                        (memberToUpdate) => memberToUpdate.original.id === member.original.id,
                                        (memberToUpdate) => editFormEditableValue(
                                            memberToUpdate,
                                            "is_primary_contact",
                                            true
                                        )
                                    ),
                                )
                            )
                        },
                        {
                            label: "Remove from case",
                            onClick: () => setMemberToBeDeleted(member),
                        },
                    ],
                    content: <CRMPartyMemberForm
                        member={member}
                        onChange={(updatedMember) => {
                            return props.onUpdateMembers(
                                pipe(
                                    props.members,
                                    extArray.updateWhere(
                                        (m) => m.original.id === updatedMember.original.id,
                                        () => updatedMember
                                    )
                                )
                            );
                        }
                        }
                        isNotAllowedToReceiveInstructEmailDueToDualRepConsent={props.isNotAllowedToReceiveInstructEmailDueToDualRepConsent}
                        onChildChange={props.onUpdateMemberChild}
                        onSendInstructedEmailClick={props.onMemberSendInstructedEmailClick}
                        onAddNewPhoneNumber={() => props.onAddNewPhoneNumber(member.original.id)}
                        onRemovePhoneNumber={props.onRemovePhoneNumber}
                        createPhoneNumberStatus={props.createPhoneNumberStatus}
                        case={props.case}
                        transactionType={props.transactionType}
                        sellerReasonForSale={props.sellerReasonForSale}
                        caseIsInstructed={props.caseIsInstructed}
                        onChildIdChecksChange={props.onChildIdChecksChange}
                        onNewIdCheck={props.onNewIdCheck}
                        onReScreenIdCheck={props.onReScreenIdCheck}
                        onRunScreeningIdCheck={props.onRunScreeningIdCheck}
                        onFlagNeedsVideoCall={props.onFlagNeedsVideoCall}
                        onSourceOfWealthChange={props.onSourceOfWealthChange}
                        onNoDigitalContactChannelExemptionChange={props.onNoDigitalContactChannelExemptionChange}
                        onSwapMember={props.onSwapMember}
                        canSignOffRoadblocks={props.canSignOffRoadblocks}
                    />
                }))
            )}
            noTabsContent={
                <CRMPadding size="tiny">
                    <TextCenter>
                        <CRMParagraph>
                            Add <WeightBold>clients</WeightBold> to get started.
                        </CRMParagraph>
                    </TextCenter>
                </CRMPadding>
            }
            afterTabsElement={
                <CRMFormButtonIconWithLabel
                    formStatus="requiresSubmission"
                    ButtonElement={(buttonProps) => <CRMButtonIcon variant="primary" {...buttonProps} />}
                    icon="person-add"
                    label="Add new client"
                    onClick={props.onAddMember}
                />
            }
        />

        {/* DELETE POPUP */}
        <CRMCardOutsidePopupBasic
            isOpen={memberToBeDeleted ? true : false}
            title="Are you sure?"
            context="important"
            onClose={() => setMemberToBeDeleted(null)}
            closeText="Cancel"
            onCTA={() => {
                if (memberToBeDeleted) {
                    props.onRemoveMember(memberToBeDeleted);
                }
                setMemberToBeDeleted(null);
            }}
            ctaText="Yes"
        >
            <CRMParagraph>
                Are you sure you want to delete this person from the party?
            </CRMParagraph>
        </CRMCardOutsidePopupBasic>
    </>;
}
